/** Name of a build folder inside a version folder that ships more than one build. */
export type WebGlBuildName = 'normal' | 'obfuscated';

/** One build of a WebGL bundle: the Embedded Wizard runtime file in its folder. */
export interface WebGlBuildManifest {
    runtime: string;
}

/**
 * Shape of a WebGL bundle's manifest.json. A version with one build lists its runtime at the
 * top level and keeps all files in the version folder. A version with an obfuscated build has
 * one key per build folder instead, e.g. { normal: { runtime }, obfuscated: { runtime } }.
 */
export interface WebGlManifest {
    runtime?: string;
    normal?: WebGlBuildManifest;
    obfuscated?: WebGlBuildManifest;
}

/** The build chosen from a manifest. */
export interface SelectedWebGlBuild {
    /** Build folder name, or null when the files are in the version folder itself. */
    name: WebGlBuildName | null;
    /** Embedded Wizard runtime filename inside the build folder. */
    runtime: string;
}

/**
 * Picks the build to load from a version's manifest: the obfuscated build when the environment
 * asks for it and the version has one, otherwise the normal build, otherwise the single build
 * of a version without build folders.
 *
 * @param manifest The parsed manifest.json of the version, or null when it could not be read.
 * @param useObfuscatedBundle The environment's webgl.useObfuscatedBundle setting.
 * @returns The build to load, or null when the manifest names no loadable build.
 */
export function selectWebGlBuild(
    manifest: WebGlManifest | null,
    useObfuscatedBundle: boolean,
): SelectedWebGlBuild | null {
    if (useObfuscatedBundle && manifest?.obfuscated?.runtime) {
        return { name: 'obfuscated', runtime: manifest.obfuscated.runtime };
    }

    if (manifest?.normal?.runtime) {
        return { name: 'normal', runtime: manifest.normal.runtime };
    }

    if (manifest?.runtime) {
        return { name: null, runtime: manifest.runtime };
    }

    return null;
}
